import { fileHref } from '../lib/fileAccess'
import { useState, useEffect, useCallback, useRef } from 'react'
import html2pdf from 'html2pdf.js'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { useAuth } from '../hooks/useAuth'
import { fetchRows, createRow, updateRow } from '../lib/api'
import DatePicker from '../components/DatePicker'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import { escHtml } from '../lib/escHtml'
import { DRILL_TYPES, DRILL_SCHEDULE } from '../lib/drillTypes'
import { AUDIT_TYPE_LABEL, GRADE_SHORT, isOpen, isOverdue, nextInternalDue, auditDate } from '../lib/ismCore'
import { shipTodayAuto } from '../lib/shipTime'
import { CAL_SELECT, derivedEvents } from '../lib/calendarCore'
import { surveyCalendarEvents } from '../lib/surveyCore'

function getVal(v) {
  if (v === null || v === undefined) return ''
  if (typeof v === 'object' && 'value' in v) return v.value
  return v
}

function parseBaserowDate(d) {
  if (!d) return ''
  const s = String(d)
  // Handle DD/MM/YYYY from Baserow UI
  if (s.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    const [day, month, year] = s.split('/')
    return year + '-' + month + '-' + day
  }
  // Already YYYY-MM-DD
  return s.split('T')[0]
}

const EVENT_COLORS = {
  'Charter': '#8a5c0a',
  'Port Call': '#2a6a96',
  'Maintenance': '#3d6cb8',
  'LSA/FFE Expiry': '#b4313c',
  'LSA/FFE Service': '#7e6624',
  'Crew': '#137050',
  'Cert Expiry': '#b4313c',
  'Inspection/Survey': '#6a5bb5',
  'Dry Dock': '#96600f',
  'Drill': '#b5502f',
  'Delivery': '#1f7a6e',
  'Owner Visit': '#6e6a10',
  'Custom': '#666c75',
  'Refit': '#2457d6',
  'Refit Overdue': '#b23a3a',
  'ISM Audit': '#76601f',
  'ISM Finding Due': '#c0434a',
  'RA Review': '#a93d6d',
  'Contract End': '#4a7a1c',
  'Passage': '#1f7480',
  'Survey Due': '#6d4bd1',
}

const EMPTY_FORM = {
  Title: '', Type: '', 'Start Date': '', 'End Date': '',
  'Start Time': '', 'End Time': '', 'All Day': true,
  Vessel: '', Notes: '', 'Reference ID': '', 'Created By': ''
}

export default function Calendar({ onNavigate, initialFilter }) {
  const { can } = useAuth()
  const { config } = useAuth()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [editRow, setEditRow] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [infoPanel, setInfoPanel] = useState(null)
  const [viewDate, setViewDate] = useState(new Date())
  const calRef = useRef(null)
  const openedRef = useRef(false)
  useEffect(() => {
    if (!initialFilter) return
    let o = null
    try { o = JSON.parse(initialFilter) } catch { const n = Number(initialFilter); if (n) o = { t: n } }
    if (!o || !o.t) return
    const d = new Date(o.t)
    const jump = () => { try { calRef.current && calRef.current.getApi().gotoDate(d) } catch {} }
    jump(); setTimeout(jump, 300)
    // once events are loaded, find the matching event and open its detail popup
    if (!openedRef.current && events && events.length) {
      const pad = n => String(n).padStart(2, '0')
      const target = d.getFullYear() + '-' + pad(d.getMonth()+1) + '-' + pad(d.getDate())
      const clean = str => String(str||'').replace(/^[^\x00-\x7F]+\s*/, '').trim()
      const label = clean(o.label || '')
      const match = (events || []).find(ev => {
        const evDate = typeof ev.start === 'string'
          ? ev.start.split('T')[0]
          : (ev.start instanceof Date ? ev.start.getFullYear()+'-'+pad(ev.start.getMonth()+1)+'-'+pad(ev.start.getDate()) : '')
        if (evDate !== target) return false
        if (!label) return true
        const ct = clean(ev.title)
        return ct === label || ct.includes(label) || label.includes(ct)
      })
      if (match && match.extendedProps) {
        openedRef.current = true
        setInfoPanel({ row: match.extendedProps.row, autoType: match.extendedProps.autoType, title: match.title, drill: match.extendedProps.drill, rec: match.extendedProps.rec, ism: match.extendedProps.ism })
      }
    }
  }, [initialFilter, events])
  const [printMode, setPrintMode] = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  const [hiddenTypes, setHiddenTypes] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('hc_cal_hidden') || '[]')) } catch { return new Set() }
  })
  function toggleType(type) {
    setHiddenTypes(prev => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type); else next.add(type)
      try { localStorage.setItem('hc_cal_hidden', JSON.stringify([...next])) } catch {}
      return next
    })
  }
  const colorToType = Object.fromEntries(Object.entries(EVENT_COLORS).map(([t, c]) => [c.toLowerCase(), t]))
  const visibleEvents = events.filter(e => {
    const t = colorToType[(e.backgroundColor || '').toLowerCase()]
    return !t || !hiddenTypes.has(t)
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [data, mtasks, crewDocs, shipDocs, charters, portCalls, marinaBookings] = await Promise.all([
        fetchRows('calendar'),
        fetchRows('mtask'),
        fetchRows('crew_docs'),
        fetchRows('docs'),
        fetchRows('charters'),
        fetchRows('port_calls'),
        fetchRows('marina_bookings'),
      ])
      const mapped = []
      ;(data || []).forEach(row => {
        const startStr = parseBaserowDate(getVal(row['Start Date']))
        const endStr = parseBaserowDate(getVal(row['End Date']))
        const color = EVENT_COLORS[getVal(row['Type'])] || '#666c75'
        const title = getVal(row['Title'])
        if (!startStr) return
        if (!endStr || startStr === endStr) {
          mapped.push({ id: String(row.id), title, start: startStr, allDay: true, backgroundColor: color, borderColor: color, extendedProps: { row } })
          return
        }
        const start = new Date(startStr + 'T12:00:00')
        const end = new Date(endStr + 'T12:00:00')
        let current = new Date(start)
        let i = 0
        while (current <= end) {
          const d = current.getFullYear() + '-' + String(current.getMonth()+1).padStart(2,'0') + '-' + String(current.getDate()).padStart(2,'0')
          mapped.push({ id: String(row.id) + '_' + i, title, start: d, allDay: true, backgroundColor: color, borderColor: color, extendedProps: { row } })
          current.setDate(current.getDate() + 1)
          i++
        }
      })
      // Auto-populate from Maintenance Tasks
      ;(mtasks || []).forEach(row => {
        const due = parseBaserowDate(getVal(row['Next Service Date']))
        if (!due) return
        const name = getVal(row['Name']) || getVal(row['Task Name']) || 'Maintenance Due'
        mapped.push({ id: 'mt_' + row.id, title: '🔧 ' + name, start: due, allDay: true, backgroundColor: EVENT_COLORS['Maintenance'], borderColor: EVENT_COLORS['Maintenance'], extendedProps: { row, autoType: 'maintenance' } })
      })

      // Auto-populate from Crew Documents expiry
      ;(crewDocs || []).forEach(row => {
        const exp = parseBaserowDate(getVal(row['Expiry Date']))
        if (!exp) return
        const name = getVal(row['Title']) || 'Cert Expiry'
        mapped.push({ id: 'cd_' + row.id, title: '📋 ' + name, start: exp, allDay: true, backgroundColor: EVENT_COLORS['Cert Expiry'], borderColor: EVENT_COLORS['Cert Expiry'], extendedProps: { row, autoType: 'cert' } })
      })

      // Auto-populate from Ship Documents expiry
      ;(shipDocs || []).forEach(row => {
        const exp = parseBaserowDate(getVal(row['Expiry Date']))
        if (!exp) return
        const name = getVal(row['Title']) || 'Doc Expiry'
        mapped.push({ id: 'sd_' + row.id, title: '📄 ' + name, start: exp, allDay: true, backgroundColor: EVENT_COLORS['Cert Expiry'], borderColor: EVENT_COLORS['Cert Expiry'], extendedProps: { row, autoType: 'doc' } })
      })

      // Auto-populate from Charters (spanning start to end)
      ;(charters || []).forEach(row => {
        const startStr = parseBaserowDate(getVal(row['Start Date']))
        const endStr = parseBaserowDate(getVal(row['End Date']))
        if (!startStr) return
        const ref = getVal(row['Charter Ref']) || 'Charter'
        const guest = getVal(row['Lead Guest'])
        const title = '🛥️ ' + ref + (guest && guest !== '-' ? ' — ' + guest : '')
        const color = EVENT_COLORS['Charter'] || '#8a5c0a'
        if (!endStr || startStr === endStr) {
          mapped.push({ id: 'ch_' + row.id, title, start: startStr, allDay: true, backgroundColor: color, borderColor: color, extendedProps: { row, autoType: 'charter' } })
          return
        }
        const start = new Date(startStr + 'T12:00:00')
        const end = new Date(endStr + 'T12:00:00')
        let current = new Date(start)
        let i = 0
        while (current <= end) {
          const d = current.getFullYear() + '-' + String(current.getMonth()+1).padStart(2,'0') + '-' + String(current.getDate()).padStart(2,'0')
          mapped.push({ id: 'ch_' + row.id + '_' + i, title, start: d, allDay: true, backgroundColor: color, borderColor: color, extendedProps: { row, autoType: 'charter' } })
          current.setDate(current.getDate() + 1)
          i++
        }
      })

      // Auto-populate from Port Calls (ETA to ETD)
      ;(portCalls || []).forEach(row => {
        const startStr = parseBaserowDate(getVal(row['ETA Date']))
        const endStr = parseBaserowDate(getVal(row['ETD Date']))
        if (!startStr) return
        const port = getVal(row['Port']) || 'Port Call'
        const title = '⚓ ' + port
        const color = EVENT_COLORS['Port Call'] || '#2a6a96'
        if (!endStr || startStr === endStr) {
          mapped.push({ id: 'pc_' + row.id, title, start: startStr, allDay: true, backgroundColor: color, borderColor: color, extendedProps: { row, autoType: 'portcall' } })
          return
        }
        const start = new Date(startStr + 'T12:00:00')
        const end = new Date(endStr + 'T12:00:00')
        let current = new Date(start)
        let i = 0
        while (current <= end) {
          const d = current.getFullYear() + '-' + String(current.getMonth()+1).padStart(2,'0') + '-' + String(current.getDate()).padStart(2,'0')
          mapped.push({ id: 'pc_' + row.id + '_' + i, title, start: d, allDay: true, backgroundColor: color, borderColor: color, extendedProps: { row, autoType: 'portcall' } })
          current.setDate(current.getDate() + 1)
          i++
        }
      })

      // Auto-populate from Marina Bookings (Arrival to Departure)
      ;(marinaBookings || []).forEach(row => {
        const startStr = parseBaserowDate(getVal(row['Arrival Date']))
        const endStr = parseBaserowDate(getVal(row['Departure Date']))
        if (!startStr) return
        const marina = getVal(row['Marina Name']) || 'Marina'
        const title = '⌂ ' + marina
        const color = '#137050'
        if (!endStr || startStr === endStr) {
          mapped.push({ id: 'mb_' + row.id, title, start: startStr, allDay: true, backgroundColor: color, borderColor: color, extendedProps: { row, autoType: 'marina' } })
          return
        }
        const start = new Date(startStr + 'T12:00:00')
        const end = new Date(endStr + 'T12:00:00')
        let current = new Date(start)
        let i = 0
        while (current <= end) {
          const d = current.getFullYear() + '-' + String(current.getMonth()+1).padStart(2,'0') + '-' + String(current.getDate()).padStart(2,'0')
          mapped.push({ id: 'mb_' + row.id + '_' + i, title, start: d, allDay: true, backgroundColor: color, borderColor: color, extendedProps: { row, autoType: 'marina' } })
          current.setDate(current.getDate() + 1)
          i++
        }
      })

      if (config && config.id) {
        const { data: lsaExp } = await supabase.from('lsa_items').select('id,name,type,expiry_date,next_service').eq('client_id', config.id).not('expiry_date','is',null)
        ;(lsaExp || []).forEach(function(item) {
          var label = item.name || item.type || 'LSA/FFE'
          var col = EVENT_COLORS['LSA/FFE Expiry']
          mapped.push({ id: 'lsa_exp_'+item.id, title: '[Expiry] '+label, start: item.expiry_date, allDay: true, backgroundColor: col, borderColor: col, extendedProps: { autoType: 'lsa_expiry', item: item } })
          if (item.next_service) {
            var c2 = EVENT_COLORS['LSA/FFE Service']
            mapped.push({ id: 'lsa_svc_'+item.id, title: '[Service] '+label, start: item.next_service, allDay: true, backgroundColor: c2, borderColor: c2, extendedProps: { autoType: 'lsa_service', item: item } })
          }
        })
        const { data: lsaSvc } = await supabase.from('lsa_items').select('id,name,type,next_service').eq('client_id', config.id).is('expiry_date',null).not('next_service','is',null)
        ;(lsaSvc || []).forEach(function(item) {
          var label = item.name || item.type || 'LSA/FFE'
          var c2 = EVENT_COLORS['LSA/FFE Service']
          mapped.push({ id: 'lsa_svc2_'+item.id, title: '[Service] '+label, start: item.next_service, allDay: true, backgroundColor: c2, borderColor: c2, extendedProps: { autoType: 'lsa_service', item: item } })
        })

        // --- Scheduled drills (SMS-16 fixed calendar) ---
        const { data: drillRecs } = await supabase.from('drill_records').select('drill_type, drill_date, crew_present, notes, pdf_url, sms_ref').eq('client_id', config.id)
        const recs = drillRecs || []
        const dcol = EVENT_COLORS['Drill']
        const nowD = new Date()
        const yr = nowD.getFullYear()
        DRILL_TYPES.forEach(function(drill) {
          if (drill.freqMonths === 0) return  // as-required: no scheduled slot
          const slots = DRILL_SCHEDULE[drill.key] || []
          slots.forEach(function(mi) {
            if (mi < nowD.getMonth()) return  // skip past months
            const doneThatMonth = recs.some(function(r) {
              if (r.drill_type !== drill.label || !r.drill_date) return false
              const d = new Date(r.drill_date + 'T00:00:00')
              return d.getFullYear() === yr && d.getMonth() === mi
            })
            if (doneThatMonth) return  // already logged, don't show as due
            const dateStr = yr + '-' + String(mi + 1).padStart(2, '0') + '-01'
            mapped.push({ id: 'drill_' + drill.key + '_' + mi, title: '\ud83d\udea8 ' + drill.label, start: dateStr, allDay: true, backgroundColor: dcol, borderColor: dcol, extendedProps: { autoType: 'drill', drill: drill } })
          })
        })

        // --- Completed drills (from the log) ---
        recs.forEach(function(r, i) {
          if (!r.drill_date) return
          const dd = new Date(r.drill_date + 'T00:00:00')
          if (dd.getFullYear() !== yr) return
          mapped.push({ id: 'drilldone_' + i, title: '\u2713 ' + r.drill_type + ' Drill', start: r.drill_date, allDay: true, backgroundColor: dcol, borderColor: dcol, extendedProps: { autoType: 'drill_done', rec: r } })
        })
      }
      // Auto-populate from Refit items (start + target dates)
      try {
        const { data: refit } = await supabase.from('refit_items').select('id,task_name,location,status,start_date,target_date').eq('client_id', config.id)
        const rToday = new Date().toISOString().slice(0,10)
        ;(refit || []).forEach(r => {
          if (r.start_date) mapped.push({ id: 'rf_s_'+r.id, title: '\ud83d\udd27 '+r.task_name+(r.location?(' \u00b7 '+r.location):''), start: r.start_date, allDay: true, backgroundColor: EVENT_COLORS['Refit'], borderColor: EVENT_COLORS['Refit'], extendedProps: { autoType: 'refit', refitId: r.id } })
          if (r.target_date) {
            const overdue = r.target_date < rToday && !['Complete','Approved'].includes(r.status)
            mapped.push({ id: 'rf_t_'+r.id, title: '\ud83c\udfaf '+r.task_name+(overdue?' (OVERDUE)':' \u2014 target'), start: r.target_date, allDay: true, backgroundColor: overdue?EVENT_COLORS['Refit Overdue']:EVENT_COLORS['Refit'], borderColor: overdue?EVENT_COLORS['Refit Overdue']:EVENT_COLORS['Refit'], extendedProps: { autoType: 'refit', refitId: r.id } })
          }
        })
      } catch (e) { /* refit events best-effort */ }
      // ISM audits (planned / held), the next internal audit due (ISM 12) and the due dates of open findings
      if (!can || can('ism_audits', 'read')) try {
        const [{ data: au }, { data: fi }, { data: st }] = await Promise.all([
          supabase.from('ism_audits').select('id,audit_no,audit_type,status,planned_date,audit_date,audit_end_date,auditor,location').eq('client_id', config.id),
          supabase.from('ism_findings').select('id,ref,grade,element,description,due_date,status,owner,audit_id').eq('client_id', config.id).neq('status', 'closed'),
          supabase.from('ism_settings').select('*').eq('client_id', config.id).maybeSingle(),
        ])
        const iToday = shipTodayAuto(), ca = EVENT_COLORS['ISM Audit'], cf = EVENT_COLORS['ISM Finding Due']
        ;(au || []).forEach(a => {
          const d = auditDate(a); if (!d) return
          const end = a.audit_end_date && a.audit_end_date > d ? new Date(new Date(a.audit_end_date + 'T12:00:00Z').getTime() + 86400000).toISOString().slice(0, 10) : undefined
          mapped.push({ id: 'ism_a_' + a.id, title: '\ud83d\udee1 ' + (a.audit_type === 'internal' ? 'ISM internal audit' : AUDIT_TYPE_LABEL[a.audit_type]) + (a.status === 'planned' ? ' (planned)' : a.status === 'closed' ? ' \u2713' : ''), start: d, end, allDay: true, backgroundColor: ca, borderColor: ca, extendedProps: { autoType: 'ism_audit', ism: a } })
        })
        const nd = nextInternalDue(au || [], st, iToday)
        if (nd.due && !nd.planned) mapped.push({ id: 'ism_due', title: '\ud83d\udee1 Internal ISM audit due' + (nd.state === 'overdue' ? ' (OVERDUE)' : ''), start: nd.due < iToday ? iToday : nd.due, allDay: true, backgroundColor: nd.state === 'overdue' ? cf : ca, borderColor: nd.state === 'overdue' ? cf : ca, extendedProps: { autoType: 'ism_due', ism: nd } })
        ;(fi || []).filter(f => isOpen(f) && f.due_date).forEach(f => {
          const late = isOverdue(f, iToday)
          mapped.push({ id: 'ism_f_' + f.id, title: '\u26a0 ' + f.ref + ' ' + (GRADE_SHORT[f.grade] || '') + ' due' + (late ? ' (OVERDUE)' : ''), start: f.due_date, allDay: true, backgroundColor: cf, borderColor: cf, extendedProps: { autoType: 'ism_finding', ism: f } })
        })
      } catch (e) { /* ISM events best-effort (module not set up yet) */ }
      // dates owned by other modules (src/lib/calendarCore.js, same list the AI reads): RA reviews, contract ends, passages
      try {
        const q = (t, mod) => (!can || can(mod, 'read')) ? supabase.from(t).select(CAL_SELECT[t]).eq('client_id', config.id) : Promise.resolve({ data: [] })
        const [{ data: ras }, { data: crw }, { data: pps }] = await Promise.all([q('risk_assessments', 'risk'), q('crew', 'crew'), q('passage_plans', 'passage')])
        const CAL_COLOR = { ra_review: EVENT_COLORS['RA Review'], contract_end: EVENT_COLORS['Contract End'], passage: EVENT_COLORS['Passage'] }
        const ICON = { ra_review: '\u26a0\ufe0f ', contract_end: '\ud83d\udcdd ', passage: '\ud83e\udded ' }
        derivedEvents({ riskAssessments: ras || [], crew: crw || [], passagePlans: pps || [] }).forEach(ev => {
          // FullCalendar's end is exclusive: show the arrival day too
          const end = ev.end ? new Date(new Date(ev.end + 'T12:00:00Z').getTime() + 86400000).toISOString().slice(0, 10) : undefined
          mapped.push({ id: ev.id, title: ICON[ev.type] + ev.title, start: ev.start, end, allDay: true, backgroundColor: CAL_COLOR[ev.type], borderColor: CAL_COLOR[ev.type], extendedProps: { autoType: ev.type, cal: ev } })
        })
      } catch (e) { /* module dates best-effort */ }
      // surveys (sql/feat-10b.sql): the next survey due of each type + the planned / open ones (src/lib/surveyCore.js)
      try {
        if (!can || can('documents', 'read')) {
          const { data: svs } = await supabase.from('vessel_surveys').select('id,survey_type,status,survey_date,place,next_due,created_at').eq('client_id', config.id)
          surveyCalendarEvents(svs || [], shipTodayAuto()).forEach(ev => {
            const late = ev.type === 'survey_due' && ev.start < shipTodayAuto(), c = late ? EVENT_COLORS['ISM Finding Due'] : EVENT_COLORS['Survey Due']
            mapped.push({ id: ev.id, title: '\ud83d\udccb ' + ev.title + (late ? ' (OVERDUE)' : ''), start: ev.start, allDay: true, backgroundColor: c, borderColor: c, extendedProps: { autoType: ev.type, cal: ev } })
          })
        }
      } catch (e) { /* surveys best-effort (not set up yet) */ }
      setEvents(mapped)
    } catch (e) { setError('Failed to load events') }
    finally { setLoading(false) }
  }, [config?.id])

  useEffect(() => { load() }, [load])

  function openAdd(dateStr) {
    setForm({ ...EMPTY_FORM, 'Start Date': dateStr || '', 'End Date': dateStr || '', Vessel: config?.vessel_name || '' })
    setEditRow(null)
    setShowModal(true)
  }

  function openEdit(info) {
    const { row, autoType } = info.event.extendedProps
    if (autoType) {
      setInfoPanel({ row, autoType, title: info.event.title, drill: info.event.extendedProps.drill, rec: info.event.extendedProps.rec, ism: info.event.extendedProps.ism, cal: info.event.extendedProps.cal })
      return
    }
    setForm({
      Title: getVal(row['Title']),
      Type: getVal(row['Type']),
      'Start Date': getVal(row['Start Date']),
      'End Date': getVal(row['End Date']),
      'Start Time': getVal(row['Start Time']),
      'End Time': getVal(row['End Time']),
      'All Day': getVal(row['All Day']),
      Vessel: getVal(row['Vessel']),
      Notes: getVal(row['Notes']),
      'Reference ID': getVal(row['Reference ID']),
      'Created By': getVal(row['Created By']),
    })
    setEditRow(row)
    setShowModal(true)
  }

  async function handleSave() {
    if (!String(form['Title'] || '').trim()) { setError('Please enter a title.'); return }
    if (!form['Start Date']) { setError('Please choose a start date.'); return }
    if (form['End Date'] && form['End Date'] < form['Start Date']) { setError('End date is before the start date.'); return }
    setSaving(true); setError('')
    try {
      if (editRow) await updateRow('calendar', editRow.id, form)
      else await createRow('calendar', form)
      setShowModal(false)
      load()
    } catch (e) { setError('Failed to save: ' + (e.message || '')) }
    finally { setSaving(false) }
  }

  async function handleDelete() {
    const label = getVal(editRow['Title']) || 'event'
    if (!confirm('Delete the event "' + label + '"? You can undo it for 10 seconds.')) return
    try {
      await deleteWithUndo(supabase, { table: 'calendar_events', id: editRow.id, label: 'event "' + label + '"', onUndone: () => load() })
      setShowModal(false)
      load()
    } catch (e) { setError('Failed to delete') }
  }

  function buildCalendarHtml() {
    const NAVY='#15294a', BLUE='#2457d6', INK='#26313c', MUTE='#5b6878', LINE='#e4e9f0', SOFT='#f6f9fc'
    const FONT="'Trebuchet MS','Segoe UI','Helvetica Neue',Arial,sans-serif"
    const vessel = escHtml((config && config.vessel_name) || 'Vessel')
    const y = viewDate.getFullYear(), mo = viewDate.getMonth()
    const monthName = viewDate.toLocaleDateString('en-GB', { month:'long', year:'numeric' })
    const evs = visibleEvents.filter(e => { const d = new Date(e.start + 'T00:00:00'); return d.getFullYear()===y && d.getMonth()===mo })
    const byDay = {}
    evs.forEach(e => { const d = new Date(e.start + 'T00:00:00').getDate(); (byDay[d] = byDay[d] || []).push(e) })
    const first = new Date(y, mo, 1)
    const startDow = (first.getDay() + 6) % 7
    const daysInMonth = new Date(y, mo+1, 0).getDate()
    const cells = []
    for (let i=0;i<startDow;i++) cells.push(null)
    for (let d=1;d<=daysInMonth;d++) cells.push(d)
    while (cells.length % 7 !== 0) cells.push(null)
    const dow = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
    const strip = (t) => escHtml((t || '').replace(/^[^\w]+\s*/, ''))   // escaped: this html is always mounted (zap-active)
    let grid = '<table style="width:100%;border-collapse:collapse;table-layout:fixed;margin-bottom:26px;">'
    grid += '<tr>' + dow.map(d => '<th style="border:1px solid '+LINE+';padding:7px 4px;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:'+MUTE+';background:'+SOFT+';">'+d+'</th>').join('') + '</tr>'
    for (let r=0;r<cells.length/7;r++) {
      grid += '<tr>'
      for (let c=0;c<7;c++) {
        const d = cells[r*7+c]
        if (!d) { grid += '<td style="border:1px solid '+LINE+';height:74px;background:#fbfcfe;"></td>'; continue }
        const dayEvs = byDay[d] || []
        const chips = dayEvs.slice(0,4).map(e => '<div style="font-size:8px;line-height:1.3;color:#fff;background:'+(e.backgroundColor||'#888')+';border-radius:3px;padding:1px 4px;margin:1px 0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">'+strip(e.title)+'</div>').join('')
        const more = dayEvs.length>4 ? '<div style="font-size:8px;color:'+MUTE+';">+'+(dayEvs.length-4)+' more</div>' : ''
        grid += '<td style="border:1px solid '+LINE+';height:74px;vertical-align:top;padding:3px 4px;"><div style="font-size:10px;font-weight:700;color:'+INK+';margin-bottom:2px;">'+d+'</div>'+chips+more+'</td>'
      }
      grid += '</tr>'
    }
    grid += '</table>'
    const sorted = evs.slice().sort((a,b) => a.start < b.start ? -1 : 1)
    const colorToType = Object.fromEntries(Object.entries(EVENT_COLORS).map(([t,c]) => [c.toLowerCase(), t]))
    let agenda = ''
    if (sorted.length) {
      agenda = '<table style="width:100%;border-collapse:collapse;"><colgroup><col style="width:120px"><col><col style="width:150px"></colgroup>'
      agenda += '<tr><th style="border:1px solid '+LINE+';padding:8px 12px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#fff;background:'+NAVY+';">Date</th><th style="border:1px solid '+LINE+';padding:8px 12px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#fff;background:'+NAVY+';">Event</th><th style="border:1px solid '+LINE+';padding:8px 12px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:#fff;background:'+NAVY+';">Type</th></tr>'
      sorted.forEach(e => {
        const dt = new Date(e.start + 'T00:00:00').toLocaleDateString('en-GB', { weekday:'short', day:'2-digit', month:'short' })
        const type = colorToType[(e.backgroundColor||'').toLowerCase()] || ''
        agenda += '<tr><td style="border:1px solid '+LINE+';padding:8px 12px;font-size:12px;color:'+INK+';">'+dt+'</td><td style="border:1px solid '+LINE+';padding:8px 12px;font-size:12px;color:'+INK+';">'+strip(e.title)+'</td><td style="border:1px solid '+LINE+';padding:8px 12px;font-size:11px;color:'+MUTE+';"><span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:'+(e.backgroundColor||'#888')+';margin-right:6px;"></span>'+type+'</td></tr>'
      })
      agenda += '</table>'
    } else { agenda = '<div style="font-size:12px;color:'+MUTE+';padding:8px 2px;">No events this month.</div>' }
    const sect = (num, title) => '<div style="margin:0 0 12px 0;padding-bottom:6px;border-bottom:2px solid '+NAVY+';"><span style="font-size:16px;font-weight:700;color:'+NAVY+';"><span style="color:'+BLUE+';">'+num+'</span>&nbsp;&nbsp;'+title+'</span></div>'
    return '<div style="width:1200px;font-family:'+FONT+';color:'+INK+';background:#fff;">'
      + '<div style="background:#fff;padding:18px 34px 14px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid '+LINE+';"><div style="display:flex;align-items:center;gap:13px;"><span style="font-size:34px;font-weight:900;color:'+BLUE+';line-height:1;letter-spacing:-3px;">\u2248</span><span style="font-size:22px;font-weight:800;letter-spacing:.13em;color:'+NAVY+';">HEYCAPTAIN</span></div><div style="font-size:10px;font-weight:700;letter-spacing:.17em;color:'+MUTE+';text-transform:uppercase;">Safety Management System</div></div>'
      + '<div style="background:'+NAVY+';padding:20px 34px;"><div style="font-size:24px;font-weight:700;color:#fff;">SCHEDULE</div><div style="font-size:12.5px;color:#aec3d6;margin-top:4px;">'+monthName+' \u00b7 '+vessel+'</div></div>'
      + '<div style="padding:24px 34px;">' + sect('1','Month Overview') + grid + sect('2','Agenda') + agenda + '</div>'
      + '<div style="border-top:1px solid '+LINE+';padding:12px 34px;display:flex;justify-content:space-between;align-items:center;margin-top:8px;"><span style="font-size:11px;font-weight:800;letter-spacing:.1em;color:'+NAVY+';">HEYCAPTAIN</span><span style="font-size:10px;color:'+MUTE+';">Hey Captain \u00b7 Superyacht Safety Management System</span></div>'
      + '</div>'
  }
  async function exportCalendarPdf() {
    setExportingPdf(true); setPrintMode(true)
    await new Promise(r => setTimeout(r, 500))
    try {
      const el = document.getElementById('cal-print')
      const monthSafe = viewDate.toLocaleDateString('en-GB', { month:'short', year:'numeric' }).replace(/[^a-zA-Z0-9]/g,'_')
      window.scrollTo(0, 0)
      const h = el ? el.scrollHeight : 900
      const opt = { margin: 6, filename: 'Schedule_'+monthSafe+'.pdf', image: { type:'jpeg', quality:0.98 }, html2canvas: { scale:2, useCORS:true, backgroundColor:'#ffffff', width:1200, windowWidth:1200, height:h, windowHeight:h, scrollX:0, scrollY:0 }, jsPDF: { unit:'mm', format:'a4', orientation:'landscape' } }
      if (el) await html2pdf().set(opt).from(el).save()
    } catch (e) {}
    setPrintMode(false); setExportingPdf(false)
  }
  return (
    <>
      <style>{`
        .fc { font-family: inherit; }
        .fc-toolbar-title { font-size: 16px !important; font-weight: 600; color: var(--text); }
        .fc-button { background: var(--surface2) !important; border: 1px solid var(--border) !important; color: var(--text) !important; font-size: 12px !important; padding: 4px 10px !important; }
        .fc-button:hover { background: var(--border) !important; }
        .fc-button-active { background: var(--gold) !important; color: #000 !important; border-color: var(--gold3) !important; }
        .fc-daygrid-day { background: var(--surface) !important; }
        .fc-daygrid-day:hover { background: var(--surface2) !important; }
        .fc-col-header-cell { background: var(--surface2) !important; color: var(--muted) !important; font-size: 11px !important; }
        .fc-daygrid-day-number { color: var(--muted) !important; font-size: 12px; }
        .fc-day-today { background: rgba(201,147,58,.08) !important; }
        .fc-day-today .fc-daygrid-day-number { color: var(--gold3) !important; font-weight: 700; }
        .fc-event { cursor: pointer; border-radius: 4px !important; font-size: 11px !important; padding: 1px 4px !important; }
        .fc-scrollgrid { border-color: var(--border) !important; }
        .fc-scrollgrid-section > td { border-color: var(--border) !important; }
        .fc-daygrid-day { border-color: var(--border) !important; }
        .fc-theme-standard td, .fc-theme-standard th { border-color: var(--border) !important; }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
        <div className="hc-cal-legend" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(EVENT_COLORS).map(([type, color]) => {
            const off = hiddenTypes.has(type)
            return (
              <div key={type} onClick={() => toggleType(type)} title={off ? 'Show ' + type : 'Hide ' + type} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: off ? 'var(--border)' : 'var(--muted)', cursor: 'pointer', userSelect: 'none', opacity: off ? 0.5 : 1, textDecoration: off ? 'line-through' : 'none', padding: '2px 4px', borderRadius: 4 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, background: off ? 'transparent' : color, border: '1px solid ' + color }} />
                {type}
              </div>
            )
          })}
        </div>
        <div className="hc-cal-actions" style={{ display:'flex', gap:8 }}>
          <button style={btnSecondary} onClick={exportCalendarPdf} disabled={exportingPdf}>{exportingPdf ? 'Exporting…' : 'Export PDF'}</button>
          {(!can || can('calendar','write')) && <button style={btnPrimary} onClick={() => openAdd('')}>+ New Event</button>}
        </div>
      </div>

      {error && <div style={{ color: '#b4313c', padding: '8px 16px', fontSize: 12 }}>{error}</div>}

      <style>{`
        @media (max-width: 1023px) {
          .hc-cal-legend { gap: 5px !important; font-size: 10px !important; max-height: 62px; overflow-y: auto; }
          .hc-cal-legend > div { font-size: 10px !important; padding: 1px 3px !important; }
          .hc-cal-actions { flex: 1 1 100% !important; }
          .hc-cal-actions > button { flex: 1 1 auto !important; }
          .hc-cal-shell { padding: 8px !important; }
          .hc-cal-shell .fc { height: 560px !important; }
          .hc-cal-shell .fc .fc-toolbar { flex-wrap: wrap !important; gap: 6px !important; }
          .hc-cal-shell .fc .fc-toolbar-title { font-size: 15px !important; }
          .hc-cal-shell .fc .fc-button { padding: 4px 7px !important; font-size: 11px !important; }
          .hc-cal-shell .fc .fc-col-header-cell-cushion { font-size: 10px !important; }
          .hc-cal-shell .fc .fc-daygrid-day-number { font-size: 11px !important; }
          .hc-cal-shell .fc .fc-event-title { font-size: 9.5px !important; }
        }
      `}</style>
      <div className="hc-cal-shell" style={{ padding: '16px 24px' }}>
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          datesSet={(arg) => { const mid = new Date((arg.start.getTime() + arg.end.getTime())/2); setViewDate(mid) }}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          events={visibleEvents}
          dateClick={(!can || can('calendar','write')) ? (info) => openAdd(info.dateStr) : undefined}
          eventClick={(!can || can('calendar','write')) ? openEdit : undefined}
          height="calc(100vh - 180px)"
          editable={false}
          dayMaxEvents={3}
          moreLinkClick="popover"
        />
      </div>

      <div id="cal-print" style={printMode ? { position:'absolute', left:0, top:0, width:1200, height:'auto', zIndex:99999, background:'#fff' } : { position:'absolute', left:-99999, top:0, width:1200, height:'auto', overflow:'hidden', opacity:0, pointerEvents:'none' }} dangerouslySetInnerHTML={{ __html: buildCalendarHtml() }} />

      {infoPanel && (
        <div style={overlay} onClick={() => setInfoPanel(null)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>{infoPanel.title}</h3>
              <button style={closeBtn} onClick={() => setInfoPanel(null)}>✕</button>
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
              {infoPanel.autoType === 'maintenance' && (
                <div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Task:</strong> {getVal(infoPanel.row['Name'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Due Date:</strong> {getVal(infoPanel.row['Next Service Date'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Status:</strong> {getVal(infoPanel.row['Status']) && (getVal(infoPanel.row['Status']).value || getVal(infoPanel.row['Status']))}</div>
                  <div style={{ marginBottom: 16 }}><strong style={{color:'var(--text)'}}>Notes:</strong> {getVal(infoPanel.row['Notes'])}</div>
                  {(!can || can('maintenance','read')) && <button style={btnPrimary} onClick={() => {
                    const taskName = getVal(infoPanel.row['Task Name']) || getVal(infoPanel.row['Name']) || ''
                    if (taskName) localStorage.setItem('hc_open_mtask', taskName)
                    setInfoPanel(null)
                    onNavigate('mtask')
                  }}>View in Maintenance Tasks →</button>}
                </div>
              )}
              {(infoPanel.autoType === 'cert' || infoPanel.autoType === 'doc') && (
                <div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Expiry Date:</strong> {getVal(infoPanel.row['Expiry Date'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Status:</strong> {getVal(infoPanel.row['Status'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Issued Date:</strong> {getVal(infoPanel.row['Issued Date'])}</div>
                  {getVal(infoPanel.row['File URL']) && <div style={{ marginBottom: 16 }}><a href={fileHref(getVal(infoPanel.row['File URL']))} target="_blank" rel="noreferrer" style={{color:'var(--gold3)'}}>📎 View Document</a></div>}
                  {(!can || (infoPanel.autoType === 'cert' ? can('crew','read') : can('documents','read'))) && <button style={btnPrimary} onClick={() => {
                    if (infoPanel.autoType === 'cert') {
                      // Extract crew name from cert title stored in Title field
                      const certTitle = getVal(infoPanel.row['Title']) || ''
                      // Title format: "Martin Morales Medical Certificate"
                      // Try Crew Member linked row first, fall back to title parsing
                      const crewMember = infoPanel.row['Crew Member']
                      const crewId = Array.isArray(crewMember) && crewMember.length > 0 ? String(crewMember[0].id) : ''
                      if (crewId) localStorage.setItem('hc_open_crew_id', crewId)
                      else localStorage.setItem('hc_open_crew_title', certTitle)
                      localStorage.setItem('hc_open_crew_tab', 'docs')
                      setInfoPanel(null)
                      onNavigate('crew')
                    } else {
                      const docTitle = getVal(infoPanel.row['Title'])
                      if (docTitle) localStorage.setItem('hc_open_doc', docTitle)
                      setInfoPanel(null)
                      onNavigate('docs')
                    }
                  }}>{infoPanel.autoType === 'cert' ? 'View in Crew →' : 'View in Documents →'}</button>}
                </div>
              )}
              {infoPanel.autoType === 'charter' && (
                <div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Guest:</strong> {getVal(infoPanel.row['Lead Guest'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Dates:</strong> {getVal(infoPanel.row['Start Date'])} → {getVal(infoPanel.row['End Date'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Route:</strong> {getVal(infoPanel.row['Embark Port'])} → {getVal(infoPanel.row['Disembark Port'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Guests:</strong> {getVal(infoPanel.row['Number of Guests'])}</div>
                  <div style={{ marginBottom: 16 }}><strong style={{color:'var(--text)'}}>Status:</strong> {getVal(infoPanel.row['Status'])}</div>
                  {(!can || can('charters','read')) && <button style={btnPrimary} onClick={() => {
                    const ref = getVal(infoPanel.row['Charter Ref'])
                    if (ref) localStorage.setItem('hc_open_charter', ref)
                    setInfoPanel(null)
                    onNavigate('charters')
                  }}>View in Charters →</button>}
                </div>
              )}
              {infoPanel.autoType === 'portcall' && (
                <div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Port:</strong> {getVal(infoPanel.row['Port'])}, {getVal(infoPanel.row['Country'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>ETA:</strong> {getVal(infoPanel.row['ETA Date'])} {getVal(infoPanel.row['ETA Time'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>ETD:</strong> {getVal(infoPanel.row['ETD Date'])} {getVal(infoPanel.row['ETD Time'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Agent:</strong> {getVal(infoPanel.row['Agent'])}</div>
                  <div style={{ marginBottom: 16 }}><strong style={{color:'var(--text)'}}>Status:</strong> {getVal(infoPanel.row['Status'])}</div>
                  {(!can || can('port_calls','read')) && <button style={btnPrimary} onClick={() => {
                    const port = getVal(infoPanel.row['Port'])
                    if (port) localStorage.setItem('hc_open_portcall', port)
                    setInfoPanel(null)
                    onNavigate('port_calls')
                  }}>View in Port Calls →</button>}
                </div>
              )}
              {infoPanel.autoType === 'marina' && (
                <div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Marina:</strong> {getVal(infoPanel.row['Marina Name'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Location:</strong> {getVal(infoPanel.row['Location'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Dates:</strong> {getVal(infoPanel.row['Arrival Date'])} → {getVal(infoPanel.row['Departure Date'])}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Berth:</strong> {getVal(infoPanel.row['Berth Number'])}</div>
                  <div style={{ marginBottom: 16 }}><strong style={{color:'var(--text)'}}>Status:</strong> {getVal(infoPanel.row['Status'])}</div>
                  {(!can || can('marina','read')) && <button style={btnPrimary} onClick={() => {
                    const marina = getVal(infoPanel.row['Marina Name'])
                    if (marina) localStorage.setItem('hc_open_marina', marina)
                    setInfoPanel(null)
                    onNavigate('marina_bookings')
                  }}>View in Marina Bookings →</button>}
                </div>
              )}
              {infoPanel.autoType === 'drill' && infoPanel.drill && (
                <div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Drill:</strong> {infoPanel.drill.label}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Frequency:</strong> {infoPanel.drill.freqMonths === 1 ? 'Monthly' : infoPanel.drill.freqMonths === 2 ? 'Every other month' : infoPanel.drill.freqMonths === 3 ? 'Quarterly' : infoPanel.drill.freqMonths === 12 ? 'Annually' : 'As required'}</div>
                  {infoPanel.drill.sms_ref && <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>SMS Ref:</strong> {infoPanel.drill.sms_ref}</div>}
                  {infoPanel.drill.code_ref && <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Code:</strong> {infoPanel.drill.code_ref}</div>}
                  {infoPanel.drill.recommended && <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' }}>Strongly recommended, not mandatory</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                    <button style={btnPrimary} onClick={() => {
                      localStorage.setItem('hc_open_checklist', 'SMS-15 Drill Report')
                      localStorage.setItem('hc_drill_prefill', infoPanel.drill.label)
                      setInfoPanel(null)
                      onNavigate('checklists')
                    }}>Run drill →</button>
                    <button style={btnSecondary} onClick={() => {
                      setInfoPanel(null)
                      onNavigate('drill_matrix')
                    }}>View in Drill Matrix →</button>
                  </div>
                </div>
              )}
              {['ism_audit', 'ism_due', 'ism_finding'].includes(infoPanel.autoType) && infoPanel.ism && (
                <div>
                  {infoPanel.autoType === 'ism_audit' && <>
                    <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Audit:</strong> {infoPanel.ism.audit_no} · {AUDIT_TYPE_LABEL[infoPanel.ism.audit_type]}</div>
                    <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Status:</strong> {infoPanel.ism.status === 'in_progress' ? 'In progress' : infoPanel.ism.status === 'closed' ? 'Closed' : 'Planned'}</div>
                    {infoPanel.ism.auditor && <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Auditor:</strong> {infoPanel.ism.auditor}</div>}
                    {infoPanel.ism.location && <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Place:</strong> {infoPanel.ism.location}</div>}
                  </>}
                  {infoPanel.autoType === 'ism_due' && <div style={{ marginBottom: 8 }}>{infoPanel.ism.text}</div>}
                  {infoPanel.autoType === 'ism_finding' && <>
                    <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>{infoPanel.ism.ref}</strong> · {GRADE_SHORT[infoPanel.ism.grade]} · ISM {infoPanel.ism.element}</div>
                    <div style={{ marginBottom: 8 }}>{infoPanel.ism.description}</div>
                    <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Due:</strong> {infoPanel.ism.due_date}{infoPanel.ism.owner ? ' · ' + infoPanel.ism.owner : ''}</div>
                  </>}
                  <button style={{ ...btnPrimary, marginTop: 8 }} onClick={() => {
                    const o = infoPanel.autoType === 'ism_audit' ? { audit: infoPanel.ism.id } : infoPanel.autoType === 'ism_finding' ? { finding: infoPanel.ism.id } : { tab: 'plan' }
                    try { localStorage.setItem('hc_open_ism', JSON.stringify(o)) } catch (e) {}
                    setInfoPanel(null)
                    onNavigate('ism_audits')
                  }}>Open in ISM Audits →</button>
                </div>
              )}
              {['survey_due', 'survey'].includes(infoPanel.autoType) && infoPanel.cal && (
                <div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>{infoPanel.autoType === 'survey_due' ? 'Next survey due:' : 'Survey date:'}</strong> {infoPanel.cal.start}</div>
                  <button style={{ ...btnPrimary, marginTop: 8 }} onClick={() => {
                    try { localStorage.setItem('hc_survey_type', infoPanel.cal.ref.survey_type); localStorage.setItem('hc_survey_open', infoPanel.cal.ref.id) } catch (e) {}
                    setInfoPanel(null)
                    onNavigate('survey_readiness')
                  }}>Open Survey Readiness →</button>
                </div>
              )}
              {['ra_review', 'contract_end', 'passage'].includes(infoPanel.autoType) && infoPanel.cal && (
                <div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Date:</strong> {infoPanel.cal.start}{infoPanel.cal.end ? ' \u2192 ' + infoPanel.cal.end : ''}</div>
                  <button style={{ ...btnPrimary, marginTop: 8 }} onClick={() => {
                    const t = infoPanel.cal.ref.table
                    if (t === 'crew') { localStorage.setItem('hc_open_crew_id', String(infoPanel.cal.ref.id)); localStorage.setItem('hc_open_crew_tab', 'info') }
                    setInfoPanel(null)
                    onNavigate(t === 'crew' ? 'crew' : t === 'risk_assessments' ? 'risk_assessments' : 'passage_plan')
                  }}>{infoPanel.cal.ref.table === 'crew' ? 'View in Crew \u2192' : infoPanel.cal.ref.table === 'risk_assessments' ? 'View in Risk Assessments \u2192' : 'View in Passage Planning \u2192'}</button>
                </div>
              )}
              {infoPanel.autoType === 'drill_done' && infoPanel.rec && (
                <div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Drill:</strong> {infoPanel.rec.drill_type}</div>
                  <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Conducted:</strong> {infoPanel.rec.drill_date}</div>
                  {infoPanel.rec.crew_present && <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Crew:</strong> {infoPanel.rec.crew_present}</div>}
                  {infoPanel.rec.sms_ref && <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>SMS Ref:</strong> {infoPanel.rec.sms_ref}</div>}
                  {infoPanel.rec.notes && <div style={{ marginBottom: 8 }}><strong style={{color:'var(--text)'}}>Notes:</strong> {infoPanel.rec.notes}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                    {infoPanel.rec.pdf_url && <a href={fileHref(infoPanel.rec.pdf_url)} target="_blank" rel="noreferrer" style={{ ...btnPrimary, textDecoration:'none', display:'inline-block' }}>View Report PDF →</a>}
                    <button style={btnSecondary} onClick={() => { setInfoPanel(null); onNavigate('drill_log') }}>View in Drill Log →</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div style={overlay} onClick={() => setShowModal(false)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>{editRow ? 'Edit Event' : 'New Event'}</h3>
              <button style={closeBtn} onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={lbl}>Title *</label>
                <input style={inp} value={form['Title']} onChange={e => setForm({ ...form, Title: e.target.value })} placeholder="e.g. Charter — Monaco" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={lbl}>Type</label>
                <select style={inp} value={form['Type']} onChange={e => setForm({ ...form, Type: e.target.value })}>
                  <option value=''>Select type...</option>
                  {Object.keys(EVENT_COLORS).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={lbl}>Created By</label>
                <input style={inp} value={form['Created By']} onChange={e => setForm({ ...form, 'Created By': e.target.value })} placeholder="Your name" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={lbl}>Start Date *</label>
                <DatePicker width="100%" value={form['Start Date']} onChange={v => setForm(f => ({ ...f, 'Start Date': v, 'End Date': (!f['End Date'] || f['End Date'] < v) ? v : f['End Date'] }))} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={lbl}>End Date</label>
                <DatePicker width="100%" value={form['End Date']} min={form['Start Date'] || undefined} onChange={v => setForm(f => ({ ...f, 'End Date': v }))} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={lbl}>Start Time</label>
                <input style={inp} type="time" value={form['Start Time']} onChange={e => setForm({ ...form, 'Start Time': e.target.value })} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={lbl}>End Time</label>
                <input style={inp} type="time" value={form['End Time']} onChange={e => setForm({ ...form, 'End Time': e.target.value })} />
              </div>
              <div style={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={lbl}>Notes</label>
                <textarea style={{ ...inp, height: 80, resize: 'vertical' }} value={form['Notes']} onChange={e => setForm({ ...form, Notes: e.target.value })} placeholder="Additional details..." />
              </div>
            </div>

            {error && <div style={{ color: '#b4313c', fontSize: 12, marginTop: 8 }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
              <div>
                {editRow && <button style={{ ...btnSm, color: '#b4313c' }} onClick={handleDelete}>Delete Event</button>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btnSecondary} onClick={() => setShowModal(false)}>Cancel</button>
                <button style={btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Event'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }
const modal = { background: 'var(--card, #fff)', color: 'var(--text)', borderRadius: 12, padding: 24, width: 'min(600px,95vw)', maxHeight: '90vh', overflowY: 'auto', border: '1px solid var(--line)', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }
const inp = { padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, width: '100%', boxSizing: 'border-box' }
const lbl = { fontSize: 11, color: 'var(--muted)' }
const closeBtn = { background: 'none', border: 'none', color: 'var(--muted)', fontSize: 18, cursor: 'pointer' }
const btnPrimary = { padding: '8px 18px', borderRadius: 6, border: 'none', background: 'var(--gold)', color: '#000', fontWeight: 600, cursor: 'pointer', fontSize: 13 }
const btnSecondary = { padding: '8px 18px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', cursor: 'pointer', fontSize: 13 }
const btnSm = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 12 }
